class ProductGallerySlider extends HTMLElement {
  #index = 0;
  #mobile = window.matchMedia("(max-width: 767px)");
  #reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  #fadeDuration = 300;
  #swipeDistance = 40;
  #swipe = null;
  #pending = null;
  #releaseTimer = null;
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const signal = this.#controller.signal;

    this.slides = Array.from(this.querySelectorAll(".product-gallery-slide"));
    this.stage = this.closest(".product-gallery-stage");
    this.dots = Array.from(this.stage?.querySelectorAll(".product-gallery-dot") || []);
    this.modelOverlay = this.stage?.querySelector(".product-gallery-slider-model-overlay");
    this.modelEntries = this.modelOverlay?.querySelector("[data-model-entries]");

    const active = this.slides.findIndex((slide) => slide.classList.contains("state--active"));
    this.setIndex(Math.max(active, 0), { silent: true });

    const variantId = Theme.URLParams?.get("variant");
    if (variantId) this.selectVariant({ id: variantId }, false);

    this.dots.forEach((dot, index) => dot.addEventListener("click", () => this.select(index), { signal }));
    document.addEventListener("productgallerythumbnails:click", (event) => this.select(Number(event.detail.index)), { signal });
    document.addEventListener("productformvariants:change", (event) => this.selectVariant(event.detail.variant), { signal });
    this.addEventListener("keydown", (event) => this.onKeydown(event), { signal });
    this.addEventListener("scroll", Theme.Helpers.debounce(() => this.onScrollEnd(), 100), { signal });
    this.addEventListener("pointerdown", (event) => this.onPointerDown(event), { signal });
    this.addEventListener("pointerup", (event) => this.onPointerUp(event), { signal });
    this.addEventListener("pointercancel", () => (this.#swipe = null), { signal });
    this.#mobile.addEventListener("change", () => this.scrollToSlide(this.#index, false), { signal });
  }

  disconnectedCallback() {
    clearTimeout(this.#releaseTimer);
    this.#controller?.abort();
  }

  selectVariant(variant, animate = true) {
    if (!variant) return;

    const index = this.slides.findIndex((slide) =>
      (slide.dataset.variantId || "").split(", ").includes(String(variant.id))
    );

    if (index < 0) return;

    if (animate) {
      this.select(index);
    } else {
      this.setIndex(index, { silent: true });
      this.scrollToSlide(index, false);
    }
  }

  async select(index) {
    if (this.slides.length < 2) return;

    const next = (index + this.slides.length) % this.slides.length;
    if (next === this.#index) return;

    if (this.#mobile.matches) {
      this.scrollToSlide(next, true);
      this.setIndex(next);
      return;
    }

    this.#pending = next;
    await this.whenLoaded(this.slides[next]);
    if (this.#pending !== next) return;

    this.holdPrevious(this.slides[this.#index]);
    this.setIndex(next);
  }

  setIndex(index, { silent = false } = {}) {
    this.#index = index;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle("state--active", active);
      slide.setAttribute("aria-hidden", String(!active));

      const video = slide.querySelector("video");
      if (!video || silent) return;

      if (active) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle("state--active", dotIndex === index);
    });

    this.updateModelOverlay();
  }

  holdPrevious(slide) {
    clearTimeout(this.#releaseTimer);
    this.querySelectorAll(".product-gallery-slide.state--previous").forEach((element) => element.classList.remove("state--previous"));

    if (!slide) return;

    slide.classList.add("state--previous");
    this.#releaseTimer = setTimeout(() => slide.classList.remove("state--previous"), this.#fadeDuration);
  }

  whenLoaded(slide) {
    const image = slide?.querySelector("img");
    if (!image || image.complete) return Promise.resolve();

    image.loading = "eager";

    return Promise.race([
      image.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 600)),
    ]);
  }

  scrollToSlide(index, animate) {
    if (!this.#mobile.matches) return;

    const smooth = animate && !this.#reducedMotion.matches;
    this.classList.toggle("state--jumping", smooth);
    this.scrollTo({ left: index * this.clientWidth, behavior: smooth ? "smooth" : "auto" });
  }

  onScrollEnd() {
    if (!this.#mobile.matches) return;

    this.classList.remove("state--jumping");
    const index = Math.round(this.scrollLeft / this.clientWidth);
    if (index !== this.#index) this.setIndex(index);
  }

  onKeydown(event) {
    if (event.target !== this) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    this.select(this.#index + (event.key === "ArrowRight" ? 1 : -1));
  }

  onPointerDown(event) {
    if (event.pointerType === "mouse" || this.#mobile.matches) return;
    this.#swipe = { x: event.clientX, y: event.clientY };
  }

  onPointerUp(event) {
    if (!this.#swipe) return;

    const distanceX = event.clientX - this.#swipe.x;
    const distanceY = event.clientY - this.#swipe.y;
    this.#swipe = null;

    if (Math.abs(distanceX) < this.#swipeDistance || Math.abs(distanceX) < Math.abs(distanceY)) return;
    this.select(this.#index + (distanceX < 0 ? 1 : -1));
  }

  updateModelOverlay() {
    const overlay = this.modelOverlay;
    if (!overlay || !this.modelEntries) return;
    if (overlay.getAttribute("data-model-sizes") !== "true") return;

    const active = this.slides[this.#index];
    const tags = String(active?.getAttribute("data-model") || "").trim().split(/\s+/).filter(Boolean);
    const sizes = tags
      .map((tag) => overlay.getAttribute(`data-model-size-${tag.replace(/\D/g, "")}`))
      .filter(Boolean);

    if (!sizes.length) {
      overlay.setAttribute("hidden", "");
      overlay.style.display = "none";
      return;
    }

    overlay.removeAttribute("hidden");
    overlay.style.display = "";
    overlay.classList.toggle("state--multiple", sizes.length > 1);
    this.modelEntries.innerHTML = sizes
      .map((size, index) => this.buildModelEntry(size, sizes.length > 1, index === sizes.length - 1))
      .join("");
  }

  buildModelEntry(value, isMultiple, isLast) {
    const parts = String(value || "").trim().split(/\s+/);
    const name = parts.length > 2 ? parts.slice(0, 2).join(" ") : "";
    let sizeText = parts.length > 2 ? parts.slice(2).join(" ") : String(value || "").trim();

    if (!isLast) sizeText = `${sizeText},&nbsp;`;

    const nameClasses = isMultiple ? "" : "visibility--desktop";
    const nameMarkup = name ? `<span class="${nameClasses}" data-model-name>${name}&nbsp;</span>` : "";

    return `<span class="flex" data-model-entry>${nameMarkup}<span data-model-size-text>${sizeText}</span></span>`;
  }
}

customElements.define("product-gallery-slider", ProductGallerySlider);

class ProductGalleryThumbnails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const element = event.target.closest("a");

      document.dispatchEvent(
        new CustomEvent("productgallerythumbnails:click", {
          detail: {
            index: element.dataset.index,
          },
        })
      );
    });
  }
}

customElements.define("product-gallery-thumbnails", ProductGalleryThumbnails);

function initProductLightbox() {
  if (window.innerWidth < 1024) return;

  const triggers = document.querySelectorAll('.lightbox-trigger');
  const lightbox = document.getElementById('lightbox');
  const closeBtn = document.getElementById('lightbox-close');

  if (!triggers.length || !lightbox || !closeBtn) return;

  triggers.forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault();
      const clickedIndex = Number.parseInt(trigger.dataset.index, 10);

      if (Number.isNaN(clickedIndex)) return;

      lightbox.classList.add('active');
      lightbox.style.cursor = 'zoom-out';
      document.body.style.overflow = 'hidden';

      const targetImage = lightbox.querySelector(`[data-lightbox-index="${clickedIndex}"]`);
      if (targetImage) {
        const offset = targetImage.offsetTop - 10;
        lightbox.scrollTo({ top: offset, behavior: 'instant' });
      }
    });
  });

  closeBtn.addEventListener('click', () => {
    closeLightbox();
  });

  lightbox.addEventListener('click', e => {
    if (e.target === lightbox) closeLightbox();
  });

  function closeLightbox() {
    lightbox.classList.remove('active');
    lightbox.style.cursor = '';
    document.body.style.overflow = '';
  }
}

document.addEventListener('DOMContentLoaded', initProductLightbox);
